import { useLayoutEffect, useRef } from "react";
import Matter from "matter-js";
import "./fallingtext.css";

const FallingText = ({
  text,
  highlightWords = [],
  highlightClass = "",
  trigger = "hover",
  backgroundColor = "transparent",
  wireframes = false,
  gravity = 0.1,
  fontSize = "2rem",
  mouseConstraintStiffness = 0.8,
}) => {
  const containerRef = useRef(null);
  const targetRef = useRef(null);
  const startedRef = useRef(false);

  const highlightKey = highlightWords.join("|").toLowerCase();
  const highlightSet = new Set(
    highlightWords.map((word) => word.toLowerCase())
  );

  useLayoutEffect(() => {
    const container = containerRef.current;
    const target = targetRef.current;

    if (!container || !target) return undefined;

    let engine;
    let runner;
    let mouseConstraint;
    let animationFrame = 0;
    let beforeUpdateHandler;
    let removeStartListener = () => {};
    const words = Array.from(
      target.querySelectorAll(".falling_text_word")
    );

    const resetWords = () => {
      words.forEach((word) => {
        word.style.position = "";
        word.style.left = "";
        word.style.top = "";
        word.style.transform = "";
      });

      target.classList.remove("is_active");
      container.classList.remove("is_active");
    };

    const stopPhysics = () => {
      removeStartListener();
      cancelAnimationFrame(animationFrame);

      if (mouseConstraint && engine) {
        Matter.Composite.remove(engine.world, mouseConstraint);
      }

      if (runner) {
        Matter.Runner.stop(runner);
      }

      if (beforeUpdateHandler && engine) {
        Matter.Events.off(engine, "beforeUpdate", beforeUpdateHandler);
      }

      if (engine) {
        Matter.Composite.clear(engine.world, false);
        Matter.Engine.clear(engine);
      }

      resetWords();
      startedRef.current = false;
    };

    const startPhysics = () => {
      if (startedRef.current || !words.length) return;
      startedRef.current = true;
      removeStartListener();

      const containerRect = container.getBoundingClientRect();
      const width = container.clientWidth;
      const height = Math.max(container.clientHeight, 320);

      engine = Matter.Engine.create();
      engine.world.gravity.y = gravity;

      const wordMetrics = words.map((word) => {
        const rect = word.getBoundingClientRect();

        return {
          word,
          width: rect.width,
          height: rect.height,
          x: rect.left - containerRect.left + rect.width / 2,
          y: rect.top - containerRect.top + rect.height / 2,
        };
      });

      const autoSpawnPositions = new Map();

      if (trigger === "auto") {
        const horizontalPadding = 36;
        const rowGap = 12;
        const bottomPadding = 18;
        let rowX = horizontalPadding;
        let rowHeight = 0;
        let rowBottom = height - bottomPadding;

        wordMetrics.forEach(({ word, width: wordWidth, height: wordHeight }) => {
          const availableWidth = width - horizontalPadding * 2;

          if (rowX + wordWidth > horizontalPadding + availableWidth) {
            rowX = horizontalPadding + Math.random() * 36;
            rowBottom -= rowHeight + rowGap;
            rowHeight = 0;
          }

          const x = Math.max(
            horizontalPadding + wordWidth / 2,
            Math.min(
              width - horizontalPadding - wordWidth / 2,
              rowX + wordWidth / 2 + (Math.random() - 0.5) * 24
            )
          );

          const y = rowBottom - wordHeight / 2 - Math.random() * 10;

          autoSpawnPositions.set(word, { x, y });

          rowX += wordWidth + 14 + Math.random() * 18;
          rowHeight = Math.max(rowHeight, wordHeight);
        });
      }

      const wordBodies = wordMetrics.map(({ word, width: wordWidth, height: wordHeight, x: flowX, y: flowY }) => {
        const spawnPosition =
          trigger === "auto" ? autoSpawnPositions.get(word) : null;
        const x = spawnPosition ? spawnPosition.x : flowX;
        const y = spawnPosition ? spawnPosition.y : flowY;

        const body = Matter.Bodies.rectangle(x, y, wordWidth, wordHeight, {
          restitution: 0.04,
          friction: 0.88,
          frictionStatic: 0.9,
          frictionAir: 0.03,
          density: 0.0022,
          slop: 0.04,
        });

        Matter.Body.rotate(body, (Math.random() - 0.5) * 0.08);

        word.style.position = "absolute";
        word.style.left = `${x - wordWidth / 2}px`;
        word.style.top = `${y - wordHeight / 2}px`;

        return { body, element: word, width: wordWidth, height: wordHeight };
      });

      const bounds = [
        Matter.Bodies.rectangle(width / 2, height + 28, width + 120, 56, {
          isStatic: true,
        }),
        Matter.Bodies.rectangle(-28, height / 2, 56, height * 2, {
          isStatic: true,
        }),
        Matter.Bodies.rectangle(width + 28, height / 2, 56, height * 2, {
          isStatic: true,
        }),
      ];

      Matter.Composite.add(engine.world, [
        ...bounds,
        ...wordBodies.map((item) => item.body),
      ]);

      const mouse = Matter.Mouse.create(container);
      mouseConstraint = Matter.MouseConstraint.create(engine, {
        mouse,
        constraint: {
          stiffness: mouseConstraintStiffness,
          render: { visible: false },
        },
      });

      Matter.Composite.add(engine.world, mouseConstraint);

      target.classList.add("is_active");
      container.classList.add("is_active");

      runner = Matter.Runner.create();
      Matter.Runner.run(runner, engine);

      beforeUpdateHandler = () => {
        wordBodies.forEach(({ body }) => {
          const flattenedAngle = body.angle * 0.92;
          const slowedSpin = body.angularVelocity * 0.76;

          Matter.Body.setAngle(body, flattenedAngle);
          Matter.Body.setAngularVelocity(body, slowedSpin);
        });
      };

      Matter.Events.on(engine, "beforeUpdate", beforeUpdateHandler);

      const syncWords = () => {
        wordBodies.forEach(({ body, element, width: wordWidth, height: wordHeight }) => {
          element.style.left = `${body.position.x - wordWidth / 2}px`;
          element.style.top = `${body.position.y - wordHeight / 2}px`;
          element.style.transform = `rotate(${body.angle}rad)`;
          element.style.zIndex = `${Math.round(body.position.y)}`;
        });

        animationFrame = requestAnimationFrame(syncWords);
      };

      syncWords();
    };

    const canHover =
      trigger === "hover" &&
      typeof window !== "undefined" &&
      window.matchMedia("(hover: hover)").matches;

    if (canHover) {
      const startOnHover = () => startPhysics();
      container.addEventListener("mouseenter", startOnHover, { once: true });
      removeStartListener = () =>
        container.removeEventListener("mouseenter", startOnHover);
    } else {
      startPhysics();
    }

    return () => {
      stopPhysics();
    };
  }, [gravity, fontSize, highlightKey, mouseConstraintStiffness, text, trigger]);

  return (
    <div
      ref={containerRef}
      className={`falling_text_container${wireframes ? " is_wireframe" : ""}`}
      style={{
        "--falling_font_size": fontSize,
        backgroundColor,
      }}
    >
      <div ref={targetRef} className="falling_text_target">
        {text.split(" ").map((word, index) => {
          const cleanWord = word.replace(/[.,!?;:]/g, "").toLowerCase();
          const isHighlighted = highlightSet.has(cleanWord);
          const className = isHighlighted
            ? `falling_text_word ${highlightClass}`.trim()
            : "falling_text_word";

          return (
            <span key={`${word}-${index}`} className={className}>
              {word}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default FallingText;
